import { Helmet } from 'react-helmet-async'

interface SEOProps {
  title?: string
  description?: string
  keywords?: string
  image?: string
  url?: string
  type?: 'website' | 'article' | 'product' | 'auction'
  price?: number
  currency?: string
  availability?: 'in stock' | 'out of stock' | 'auction'
  category?: string
  brand?: string
  condition?: string
  noIndex?: boolean
  canonical?: string
}

const defaultSEO = {
  title: 'EvoltBay - Solar Energy Ecommerce | Enterprise Quotation System | Government Solar Contracting',
  description: 'EvoltBay is the leading solar energy ecommerce platform and enterprise quotation system for solar equipment. Specializing in government solar contracting, we connect buyers with certified vendors for solar panels, inverters, batteries, and complete solar systems.',
  keywords: 'solar energy ecommerce, enterprise quotation system, government solar contracting, quotation system, solar marketplace, solar panels, solar equipment, renewable energy, solar inverters, solar batteries, solar procurement, solar contracting, solar vendors, solar RFQ, solar quotes',
  image: '/images/evoltbay-og-image.jpg',
  url: 'https://evoltbay.com',
  type: 'website'
}

export default function SEO({
  title,
  description,
  keywords,
  image,
  url,
  type = 'website',
  price,
  currency = 'USD',
  availability,
  category,
  brand,
  condition,
  noIndex = false,
  canonical
}: SEOProps) {
  const pageTitle = title 
    ? `${title} | Evoltbay`
    : defaultSEO.title
  
  const seoDescription = description || defaultSEO.description
  const seoKeywords = keywords || defaultSEO.keywords
  const seoImage = image || defaultSEO.image
  const seoUrl = url || defaultSEO.url
  
  // Ensure absolute URL for image
  const absoluteImage = seoImage.startsWith('http') 
    ? seoImage 
    : `${window.location.origin}${seoImage}`

  // Generate structured data based on type
  const getStructuredData = () => {
    const baseData = {
      "@context": "https://schema.org",
      "@type": type === 'product' || type === 'auction' ? 'Product' : 'WebPage',
      "name": title || defaultSEO.title,
      "description": seoDescription,
      "url": seoUrl,
      "image": absoluteImage
    }

    if (type === 'product' || type === 'auction') {
      return {
        ...baseData,
        "@type": "Product",
        "brand": brand ? { "@type": "Brand", "name": brand } : undefined,
        "category": category,
        "condition": condition,
        "offers": price ? {
          "@type": type === 'auction' ? "AggregateOffer" : "Offer",
          "price": price,
          "priceCurrency": currency,
          "availability": `https://schema.org/${availability === 'in stock' ? 'InStock' : availability === 'out of stock' ? 'OutOfStock' : 'LimitedAvailability'}`,
          "seller": {
            "@type": "Organization",
            "name": "Evoltbay"
          }
        } : undefined
      }
    }

    return baseData
  }

  // Generate Organization structured data (always included)
  const getOrganizationData = () => ({
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "EvoltBay",
    "alternateName": "Evoltbay",
    "url": "https://evoltbay.com",
    "logo": `${window.location.origin}/icons/icon-512x512.png`,
    "description": "Leading solar energy ecommerce platform and enterprise quotation system. Specializing in government solar contracting, connecting buyers with certified vendors for solar equipment procurement.",
    "sameAs": [],
    "contactPoint": {
      "@type": "ContactPoint",
      "contactType": "Customer Service",
      "availableLanguage": ["English"]
    },
    "areaServed": {
      "@type": "Country",
      "name": "United States"
    },
    "knowsAbout": [
      "Solar Energy Ecommerce",
      "Enterprise Quotation System",
      "Government Solar Contracting",
      "Solar Equipment Procurement",
      "Solar Panels",
      "Solar Inverters",
      "Solar Batteries",
      "Renewable Energy"
    ]
  })

  // Generate Website structured data (for homepage)
  const getWebsiteData = () => {
    if (type === 'website' && (!url || url === defaultSEO.url || url.includes('evoltbay.com'))) {
      return {
        "@context": "https://schema.org",
        "@type": "WebSite",
        "name": "EvoltBay",
        "url": "https://evoltbay.com",
        "description": "Leading solar energy ecommerce platform and enterprise quotation system for government solar contracting",
        "potentialAction": {
          "@type": "SearchAction",
          "target": {
            "@type": "EntryPoint",
            "urlTemplate": "https://evoltbay.com/search?q={search_term_string}"
          },
          "query-input": "required name=search_term_string"
        },
        "keywords": "solar energy ecommerce, enterprise quotation system, government solar contracting, quotation system, solar marketplace"
      }
    }
    return null
  }

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{pageTitle}</title>
      <meta name="description" content={seoDescription} />
      <meta name="keywords" content={seoKeywords} />
      
      {/* Canonical URL */}
      {canonical && <link rel="canonical" href={canonical} />}
      
      {/* Robots */}
      {noIndex && <meta name="robots" content="noindex,nofollow" />}
      
      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={seoDescription} />
      <meta property="og:image" content={absoluteImage} />
      <meta property="og:url" content={seoUrl} />
      <meta property="og:site_name" content="Evoltbay" />
      
      {/* Product-specific Open Graph */}
      {(type === 'product' || type === 'auction') && price && (
        <>
          <meta property="product:price:amount" content={price.toString()} />
          <meta property="product:price:currency" content={currency} />
          <meta property="product:availability" content={availability || 'in stock'} />
          {category && <meta property="product:category" content={category} />}
          {condition && <meta property="product:condition" content={condition} />}
        </>
      )}
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@Evoltbay" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={seoDescription} />
      <meta name="twitter:image" content={absoluteImage} />
      
      {/* Additional Meta Tags */}
      <meta name="author" content="Evoltbay" />
      <meta name="theme-color" content="#2563eb" />
      <meta name="msapplication-TileColor" content="#2563eb" />
      
      {/* JSON-LD Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(getStructuredData(), null, 2)}
      </script>
      
      {/* Organization Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(getOrganizationData(), null, 2)}
      </script>
      
      {/* Website Structured Data (for homepage) */}
      {getWebsiteData() && (
        <script type="application/ld+json">
          {JSON.stringify(getWebsiteData(), null, 2)}
        </script>
      )}
    </Helmet>
  )
} 